'use client';

import type { ChangeEvent, Ref } from 'react';
import React, { useImperativeHandle, useLayoutEffect, useRef } from 'react';

import { twMerge } from 'tailwind-merge';

import styles from './CheckboxInput.module.css';

type OuterProps = React.JSX.IntrinsicElements['div'];

export type CheckboxInputProps = {
  checked?: boolean;
  disabled?: boolean;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  indeterminate?: boolean;
  disableStopPropagation?: boolean;
  ref?: Ref<HTMLInputElement>;
} & Omit<OuterProps, 'onChange'>;

export const CheckboxInput = (props: CheckboxInputProps) => {
  const {
    checked = false,
    disabled = false,
    indeterminate = false,
    onChange,
    className,
    children,
    disableStopPropagation = false,
    ref,
    ...otherProps
  } = props;

  const checkboxRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => checkboxRef.current!);

  useLayoutEffect(() => {
    if (!checkboxRef.current) {
      return;
    }
    checkboxRef.current.indeterminate = !checked && indeterminate;
  }, [checked, indeterminate]);

  return (
    <div
      className={twMerge(
        styles.CheckboxListItem,
        'flex items-center',
        !disabled && 'cursor-pointer',
        className,
      )}
      data-testid="CheckboxInput"
      {...otherProps}
    >
      <div className="flex items-center">
        <input
          aria-checked={indeterminate && !checked ? 'mixed' : checked}
          checked={checked}
          className="mr-2 cursor-pointer accent-orange-default"
          disabled={disabled}
          onChange={onChange}
          onClick={(e) => {
            if (!disableStopPropagation) {
              e.stopPropagation();
            }
          }}
          onMouseDown={(e) => {
            if (!disableStopPropagation) {
              e.stopPropagation();
            }
          }}
          ref={checkboxRef}
          type="checkbox"
        />
      </div>
      <div className={twMerge(disabled && 'text-text-disabled')}>{children}</div>
    </div>
  );
};
